import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  TextField,
  Theme,
  Typography,
  useTheme
} from "@material-ui/core";
import clsx from 'clsx';
import React, {useEffect, useState} from "react";
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import Draggable from "react-draggable";
import { headerHeight } from "../app";
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { nanoid } from "@reduxjs/toolkit";
import { isEqual } from 'lodash';
import { useTranslation } from "react-i18next";

export interface DropDownInfo {
  value: string;
  title: string;
  entries: string[];
  text: string;
}

const minDialogWidth = 300; // Used for prompt, dropdown, text... etc

type DialogWidth = "xs" | "sm" | "md" | "lg" | "xl"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    draggableTitleClose: {
      color: theme.palette.grey[500],
      marginRight: theme.spacing(1)
    },
    draggableTitleMinimize: {
      color: theme.palette.grey[500],
    },
    draggableContentStaticHeight: {
      height: `min(75vh, 500px) !important`,
    },
    draggableContent: {
      padding: theme.spacing(0),
      paddingTop: "0px !important",
      height: "fit-content",
      width: "fit-content",
      maxWidth: 'none',
      maxHeight: 'none'
    },
    draggableMouseDown: {
      "z-index": `${theme.zIndex.modal + 1} !important`
    },
    draggableContentParent: {
      display: "flex",
      flexDirection: "column",
      minWidth: "400px",
      maxWidth: "75vw",
      minHeight: "200px",
      maxHeight: "75vh",
      overflow: "hidden",
      position: "relative",
      resize: "both",
      zIndex: 1 // Fixes slimscroll visual issues
    },
    draggableResizeHandler: {
      float: "right",
      position: "absolute",
      bottom: "0px",
      right: "0px"
    },
    draggableTopDiv: {
      display: "flex",
      alignItems: "center"
    },
    draggableBackdropPaper: {
      height: "inherit"
    },
    draggableBackdropRounded: {
      borderRadius: theme.spacing(0.5),
      height: "inherit",
    },
    draggableRoundCorner: {
      backgroundColor: "transparent"
    },
    draggableTitle: {
      userSelect: "none",
      cursor: "grab",
      flex: "1 !important"
    },
    draggableTitleDown: {
      cursor: "grabbing"
    },
    progressContent: {
      padding: `${theme.spacing(3)}px !important`,
    },
    progressNoTransition: {
      transition: "none"
    },
    dropdownGrid: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "& span": {
        flex: 1
      }
    },
    dropdownSpacing: {
      marginBottom: theme.spacing(2)
    },
    simpleDialogMain: {
      "z-index": `${theme.zIndex.modal + 1} !important`
    },
    simpleDialogHeader: {
      display: "flex",
      alignItems: "center"
    },
    simpleDialogTitle: {
      userSelect: "none",
      flex: 1
    },
    simpleDialogEdit: {
      color: theme.palette.grey[500],
    },
    simpleDialogClose: {
      color: theme.palette.grey[500],
      marginRight: theme.spacing(1)
    }
  }),
);

interface PromptDialogProps {
  open: boolean;
  title: string;
  prompt: string;
  button: string;
  onCancel(): void;
  onSubmit(): void;
}

export const PromptDialog = (props: PromptDialogProps) => {
  const {open, title, prompt, button, onCancel, onSubmit} = props;
  const {t} = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <DialogTitle>
        {title}
      </DialogTitle>

      <DialogContent style={{
        minWidth: minDialogWidth
      }}>
        <DialogContentText>
          {prompt}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
        >
          {button}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface AlertDialogProps {
  open: boolean;
  error?: boolean
  title: string;
  onClose(): void;
  children: any;
}

export const AlertDialog = (props: AlertDialogProps) => {
  const {open, onClose, title, error=false} = props;

  const {t} = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <div style={{
        display: "flex",
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(1.5)
      }}>
        {error && <ErrorOutlineIcon style={{
          margin: "auto 0",
          fontSize: "2em",
          color: theme.palette.error.main
        }}/>}

        {!error && <CheckCircleOutlineIcon style={{
          margin: "auto 0",
          fontSize: "2em",
          color: theme.palette.success.main
        }}/>}

        <DialogTitle>
          {title}
        </DialogTitle>
      </div>

      <DialogContent>
        {props.children}
      </DialogContent>

      <DialogActions style={{
        paddingTop: theme.spacing(0)
      }}>
        <Button onClick={onClose} color="primary">
          {t("buttons.dismiss")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TextDialogProps {
  open: boolean;
  title: string;
  label: string;
  prompt: string;
  placeholder?: string;
  onCancel(): void;
  onSubmit(value: string): void;
  clear?: boolean;
}

export const TextDialog = (props: TextDialogProps) => {
  const {open, onSubmit, onCancel, label, prompt,
    placeholder="", clear=false} = props;

  const [text, setText] = useState("");
  const [disabled, setDisabled] = useState(true);
  const {t} = useTranslation();

  const handleTextChange = (event) => {
    setText(event.target.value);
  };

  useEffect(() => {
    if (text.trim() !== '') {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [text]);

  useEffect(() => {
    setText(placeholder);
  }, [open]);

  const handleCancel = () => {
    onCancel();
    setText(placeholder);
  };

  const handleSubmit = () => {
    const trimmedText = text.trim();

    onSubmit(trimmedText);

    if (clear) {
      setText('');
    } else {
      setText(trimmedText);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (text === '') {
        return;
      }

      handleSubmit();
    }
  };

  return (
    <Dialog open={props.open}>
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent style={{
        minWidth: minDialogWidth
      }}>
        <DialogContentText>
          {prompt}
        </DialogContentText>
        <TextField
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder={placeholder}
          margin="dense"
          label={label}
          type="text"
          fullWidth
          value={text}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={disabled}>
          {t("buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DropdownDialogProps {
  open: boolean;
  title: string;
  prompt: string;
  onCancel(): void;
  onSubmit(value: string): void;
  options: [value: any, text: string][];
}

export const DropDownDialog = (props: DropdownDialogProps) => {
  const {open, title, prompt, options, onCancel, onSubmit} = props;

  const [value, setValue] = useState(options[0][0]);
  const {t} = useTranslation();

  const resetDefaults = () => {
    setValue(options[0][0]);
  };

  const handleSubmit = () => {
    onSubmit(value);
  };

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  useEffect(() => {
    if (!open) return;
    resetDefaults();
  }, [open]);

  return (
    <Dialog open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent style={{
        minWidth: minDialogWidth
      }}>
        <DialogContentText>
          {prompt}
        </DialogContentText>
        <Select
          value={value}
          fullWidth
          onChange={handleChange}
        >
          {options.map((option, index) => {
            const [value, text] = option;

            return (
              <MenuItem key={index} value={value}>
                {text}
              </MenuItem>
            );
          })}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary">
          {t("buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface SimpleDialogProps {
  open: boolean
  title: string
  button?: string
  onSubmit?(): void
  onClose(): void
  onEdit?(): void
  canSubmit?: boolean
  canClose?: boolean
  children: any
  maxWidth?: DialogWidth
}

export const SimpleDialog = (props: SimpleDialogProps) => {
  const {title, button = "Submit", open, onSubmit, onClose, onEdit,
    canSubmit=true, canClose=true, children, maxWidth="sm", ...other} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={maxWidth}
      className={classes.simpleDialogMain}
      {...other}
    >
      <div className={classes.simpleDialogHeader}>
        <DialogTitle className={classes.simpleDialogTitle}>
          {title}
        </DialogTitle>

        {!!onEdit && (
          <IconButton
            className={classes.simpleDialogEdit}
            onClick={onEdit}
          >
            <EditIcon fontSize="small"/>
          </IconButton>
        )}

        {/* Close button */}
        <IconButton
          className={classes.simpleDialogClose}
          disabled={!canClose}
          onClick={onClose}
        >
          <CloseIcon fontSize="small"/>
        </IconButton>
      </div>

      <DialogContent>
        {children}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={!canClose}
          color="primary"
        >
          {t('buttons.cancel')}
        </Button>

        {!!onSubmit && (
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            color="primary"
          >
            {button}
          </Button>
        )}
      </DialogActions>

    </Dialog>
  );
};

interface DraggableDialogProps {
  open: boolean
  title: string
  onClose?(): void
  initialHeight?: number | string
  initialWidth?: number | string
  resize?: boolean
  children: any
}

export const DraggableDialog = (props: DraggableDialogProps) => {
  const {open, title, initialHeight, initialWidth, onClose} = props;

  const classes = useStyles();
  const theme = useTheme();

  const initialPositionX = theme.spacing(2);
  const initialPositionY = headerHeight + theme.spacing(2);

  const paperPropID = useState(nanoid())[0];
  const resizeElementID = useState(nanoid())[0];
  const [bounds, setBounds] = useState(null);
  const [mouseDown, setMouseDown] = useState(false);

  const [modifiedSize, setModifiedSize] = useState({
    x: initialWidth ? initialWidth : "450px",
    y: initialHeight ? initialHeight : "fit-content"
  });

  const [position, setPosition] = useState({
    x: initialPositionX,
    y: initialPositionY
  });

  const onDragMouseDown = () => {
    setMouseDown(true);
  };

  const onDragMouseUp = () => {
    setMouseDown(false);
  };

  const getNewBoundary = (elementWidth) => {
    let newBounds = {
      right : window.innerWidth - elementWidth,
      left : 0,
      bottom : window.innerHeight - 40,
      top : headerHeight
    };

    if (newBounds.right - newBounds.left < elementWidth) {
      newBounds.right = window.innerWidth - elementWidth;
      newBounds.left = 0;
    }

    // Add bounds padding
    const boundsPadding = 10;
    newBounds.right -= boundsPadding;
    newBounds.left += boundsPadding;
    newBounds.top += boundsPadding;

    return newBounds;
  };

  const checkOutOfBounds = (position, newBounds) => {
    let x = position.x;
    let y = position.y;

    const rightOutOfBounds = (x > newBounds.right);
    const leftOutOfBounds = (x < newBounds.left);
    const bottomOutOfBounds = (y > newBounds.bottom);
    const topOutOfBounds = (y < newBounds.top);

    if (rightOutOfBounds) {
      x = Math.min(x, newBounds.right);
    }

    if (leftOutOfBounds) {
      x = Math.max(newBounds.left, x);
    }

    if (bottomOutOfBounds) {
      y = Math.min(y, newBounds.bottom);
    }

    if (topOutOfBounds) {
      y = Math.max(y, newBounds.top);
    }

    return {x, y};
  };

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      let element = document.getElementById(resizeElementID);
      if (!element) return;

      // Remember our resized width and height so it can be applied
      // the next time that the dialog is opened
      const elementWidth = element.clientWidth;
      const elementHeight = element.clientHeight;

      const newModifiedSize = {
        x: `${elementWidth}px`,
        y: `${elementHeight}px`
      };

      // Calculate new boundary values
      const newBounds = getNewBoundary(elementWidth);

      // Move dialog if we are out of bounds
      const newPosition = checkOutOfBounds(position, newBounds);

      /** NOTE: React is bad with object equality, so lodash is used */

      if (!isEqual(modifiedSize, newModifiedSize)) {
        setModifiedSize(newModifiedSize);
      }

      if (!isEqual(bounds, newBounds)) {
        setBounds(newBounds);
      }

      if (!isEqual(position, newPosition)) {
        setPosition(newPosition);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [open, bounds, position, modifiedSize]);

  return (
    <Draggable
      handle=".draggable-dialog-title"
      position={position}
      bounds={bounds}
      onDrag={(event, data) => {
        const {x,y} = data;
        setPosition({x, y});
      }}
    >
      <Dialog
        fullScreen
        maxWidth="lg"
        open={open}
        onFocus={() => {
          /** Remove tabindex from paper to stop drag/drop issues */
          let element = document.getElementById(paperPropID);
          element?.parentElement.removeAttribute("tabindex");
        }}
        aria-labelledby="draggable-dialog-title"
        className={clsx(classes.draggableContent, {
          [classes.draggableMouseDown]: mouseDown
        })}
        classes={{
          paperFullScreen: classes.draggableBackdropRounded,
          scrollPaper: classes.draggableBackdropPaper
        }}
        BackdropProps={{
          className: classes.draggableRoundCorner
        }}
        PaperProps={{
          id: paperPropID
        }}
      >
        <div
          onMouseDown={onDragMouseDown}
          onMouseUp={onDragMouseUp}
          className={classes.draggableTopDiv}
        >
          {/* Draggable title */}
          <DialogTitle
            className={clsx("draggable-dialog-title", classes.draggableTitle, {
              [classes.draggableTitleDown]: mouseDown,
            })}
          >
            {title}
          </DialogTitle>

          {/* Close button */}
          <IconButton
            aria-label="close"
            className={classes.draggableTitleClose}
            onClick={onClose}
          >
            <CloseIcon fontSize="small"/>
          </IconButton>
        </div>

        <div
          id={resizeElementID}
          style={{
            width: modifiedSize.x,
            height: modifiedSize.y
          }}
          className={classes.draggableContentParent}
        >
          {props.children}
        </div>

      </Dialog>
    </Draggable>
  );
};

interface ProgressDialogProps {
  open: boolean;
  text: string;
  percent: number;
  animated?: boolean
}

export const ProgressDialog = (props: ProgressDialogProps) => {
  const {open, text, percent, animated=false} = props;
  const classes = useStyles();

  return (
    <Dialog open={open}>
      <DialogContent className={classes.progressContent}>
        <DialogContentText>
          {text} <b>{percent.toFixed(2)}%</b>
        </DialogContentText>
        <LinearProgress
          variant="determinate"
          value={percent}
          classes={{
            bar: clsx("", {
              [classes.progressNoTransition]: !animated
            })
          }}
          color="secondary"
        />
      </DialogContent>
    </Dialog>
  );
};

export const MultiDropDownDialog = props => {
  const [dropDowns, setDropDowns] = useState(props.dropDowns);
  const classes = useStyles();
  const {t} = useTranslation();

  const handleCancel = () => {
    props.onCancel();
  };

  useEffect(() => {
    let tempDropDowns = dropDowns;
    setDropDowns(tempDropDowns);
  }, []);

  const handleSubmit = () => {
    props.onSubmit(dropDowns);
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>, key) => {
    let tempDropDown: {[key: string]: DropDownInfo} = {...dropDowns};
    tempDropDown[key].value = event.target.value as string;
    setDropDowns(tempDropDown);
  };

  return (
    <Dialog fullWidth maxWidth={props.maxWidth} open={props.open} aria-labelledby="form-dialog-title">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={props.spacing} >
          {Object.keys(dropDowns).map(key => {
            let dropDown = dropDowns[key] as DropDownInfo;

            return (
              <Grid
                key={key}
                item
                xs={props.gridSize}
                className={classes.dropdownGrid}
              >
                <InputLabel id={dropDown.title}>
                  {dropDown.title}
                </InputLabel>

                <Typography variant="caption">
                  {dropDown.text}
                </Typography>

                <Select
                  fullWidth
                  id={dropDown.title + '_id'}
                  value={dropDown.value}
                  onChange={(event) => {
                    handleChange(event, key);
                  }}
                  className={classes.dropdownSpacing}
                >
                  {dropDown.entries.map(dropDownEntry =>
                    <MenuItem
                      value={dropDownEntry}
                      key={dropDownEntry + dropDown.title}
                    >
                      {dropDownEntry}
                    </MenuItem>
                  )}

                </Select>
              </Grid>
            );
          })}
        </Grid>

      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t("buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary">
          {t("buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
